import type { TCustomModelNumber, TCustomModelNumberSummary } from "types/customModelNumbers.type"
import type { TDimensions, TSelectableModelNumber } from "types/modelNumbers.type"

/**
 * Pure adapters between a Custom Model Number definition (#1346) and the shapes the existing
 * model-number UI already speaks. They exist so the picker's Custom tab can reuse `ModelNumberRow`
 * and `computeMatch` unchanged instead of growing a parallel row component and a parallel matcher.
 */

/**
 * A custom definition's dimensions in catalogue form.
 *
 * A Stevens model number carries an ARRAY of allowed values or ranges per axis (`["20-30", "36"]`);
 * a custom definition carries one integer. Wrapping each in a single-entry array makes it an exact
 * match rule, which is what `dimensionEntryMatches` reads it as — so the dimension badges on a
 * custom row mean exactly what the issue asks: the object's W/H/D equals the definition's, or it
 * does not.
 *
 * `avg_size` / `min_size` are empty strings rather than derived numbers: nothing in the custom flow
 * consumes them, and inventing values would make them look authoritative.
 */
export const customModelNumberDimensions = (
	custom: Pick<TCustomModelNumberSummary, "width" | "height" | "depth">,
): TDimensions => ({
	width: [String(custom.width)],
	height: [String(custom.height)],
	depth: [String(custom.depth)],
	avg_size: "",
	min_size: "",
})

/**
 * A custom definition as a selectable row.
 *
 * `image_from_stevens_catalogue` is null by construction — a custom object has no catalogue image,
 * which is the same reason its sidebar shows the "images are not supported" placeholder.
 */
export const toSelectableCustomModelNumber = (custom: TCustomModelNumberSummary): TSelectableModelNumber => ({
	id: custom.id,
	model_number: custom.model_number,
	image_from_stevens_catalogue: null,
	dimensions: customModelNumberDimensions(custom),
})

/**
 * Substring search over a custom definition, matching the picker's other two scopes.
 *
 * Widened to the NAME as well as the number on purpose: a custom definition is something the
 * estimator wrote themselves ("Shortened wall cabinet"), so the name is often the only part they
 * remember. An empty term matches everything, so the tab browses by default.
 */
export const customModelNumberMatchesTerm = (custom: TCustomModelNumber, term: string): boolean => {
	const needle = term.trim().toLowerCase()
	if (needle === "") return true
	return (
		custom.model_number.toLowerCase().includes(needle) ||
		custom.name.toLowerCase().includes(needle)
	)
}
